<template>
  <Modal :show="show" @close="handleClose" size="md" :close-on-backdrop="true" :z-index="10100">
    <template #header>
      <div class="usage-modal__header-row">
        <h2 id="modal-title" class="usage-modal__modal-title">Credits Required</h2>
        <div v-if="trialDaysRemaining && trialDaysRemaining > 0" class="usage-modal__trial-badge">
          <Icon icon="mdi:clock-outline" class="usage-modal__clock-icon" />
          <span
            >{{ trialDaysRemaining }} day{{ trialDaysRemaining === 1 ? "" : "s" }} left in
            trial</span
          >
        </div>
      </div>
    </template>
    <div class="usage-modal">
      <div class="usage-modal__content">
        <p class="usage-modal__text">
          Feel free to explore all our features! Your plan includes LinkedIn, WhatsApp, and Email
          integrations. However,
          <strong>call minutes</strong>, <strong>SMS messages</strong>,
          <strong>phone &amp; email enrichment</strong>, and
          <strong>additional phone numbers</strong> beyond your included quota are usage-based and
          deducted from your credit balance &mdash; you only pay for what you use.
        </p>
      </div>

      <div class="usage-modal__balance-strip">
        <Icon icon="mdi:wallet-outline" class="usage-modal__balance-icon" />
        <span>
          Current credit balance:
          <strong :class="{ 'usage-modal__balance--low': balanceCents < 200 }">
            {{ formattedBalance }}
          </strong>
        </span>
        <span v-if="balanceCents < 200" class="usage-modal__balance-note">
          &mdash; $2.00 minimum required
        </span>
      </div>

      <div class="usage-modal__features">
        <div class="usage-modal__feature-item">
          <Icon icon="mdi:phone-outline" class="usage-modal__feature-icon" />
          <span>Call minutes</span>
        </div>
        <div class="usage-modal__feature-item">
          <Icon icon="mdi:message-text-outline" class="usage-modal__feature-icon" />
          <span>SMS messages</span>
        </div>
        <div class="usage-modal__feature-item">
          <Icon icon="mdi:account-search-outline" class="usage-modal__feature-icon" />
          <span>Contact enrichment</span>
        </div>
        <div class="usage-modal__feature-item">
          <Icon icon="mdi:phone-plus-outline" class="usage-modal__feature-icon" />
          <span>Extra phone numbers</span>
        </div>
        <div class="usage-modal__feature-item">
          <Icon icon="mdi:account-plus-outline" class="usage-modal__feature-icon" />
          <span>Team seats</span>
        </div>
      </div>

      <template v-if="!hasPaymentMethod">
        <p class="usage-modal__select-label">Select amount to add</p>

        <div class="usage-modal__presets">
          <button
            v-for="preset in presets"
            :key="preset.cents"
            :class="[
              'usage-modal__preset-btn',
              {
                'usage-modal__preset-btn--selected':
                  !useCustomAmount && selectedAmountCents === preset.cents,
              },
            ]"
            @click="selectPreset(preset.cents)"
          >
            {{ preset.label }}
          </button>
          <button
            :class="[
              'usage-modal__preset-btn',
              'usage-modal__preset-btn--custom',
              { 'usage-modal__preset-btn--selected': useCustomAmount },
            ]"
            @click="activateCustom"
          >
            <Icon icon="mdi:pencil-outline" width="13" height="13" />
            Custom
          </button>
        </div>

        <div v-if="useCustomAmount" class="usage-modal__custom-wrap">
          <div class="usage-modal__custom-field">
            <span class="usage-modal__currency-prefix">$</span>
            <input
              ref="customInputRef"
              type="number"
              v-model.number="customAmountDollars"
              min="20"
              max="2000"
              step="1"
              class="usage-modal__custom-input"
              placeholder="20"
            />
          </div>
          <span class="usage-modal__hint">Min $20 &middot; Max $2,000</span>
        </div>
      </template>
    </div>

    <template #footer>
      <Button variant="secondary" class="usage-modal__footer-btn" @click="handleClose">
        Maybe Later
      </Button>
      <Button
        v-if="hasProceedAction"
        class="usage-modal__footer-btn usage-modal__cta-btn"
        @click="emit('proceed')"
      >
        Proceed
      </Button>
      <Button
        v-else-if="hasPaymentMethod"
        class="usage-modal__footer-btn usage-modal__cta-btn"
        @click="emit('purchase-credits')"
      >
        <Icon icon="mdi:wallet-plus-outline" class="usage-modal__btn-icon" />
        Purchase Credits
      </Button>
      <Button
        v-else
        class="usage-modal__footer-btn usage-modal__cta-btn"
        @click="handleAddPaymentMethod"
        :disabled="!isValidAmount || isCheckingOut"
      >
        <Icon v-if="!isCheckingOut" icon="mdi:credit-card-outline" class="usage-modal__btn-icon" />
        <Icon v-else icon="mdi:loading" class="usage-modal__btn-icon usage-modal__btn-icon--spin" />
        {{ isCheckingOut ? "Processing..." : `Add Card &amp; Purchase ${formattedSelectedAmount}` }}
      </Button>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick } from "vue";
import Modal from "@/components/ui/Modal.vue";
import Button from "@/components/ui/Button.vue";
import { Icon } from "@iconify/vue";
import Billing from "@/services/billing/billing";
import { toastService } from "@/utils/const";

interface Props {
  show: boolean;
  hasProceedAction?: boolean;
  hasPaymentMethod?: boolean;
  balanceCents?: number;
  trialDaysRemaining?: number;
}

const props = withDefaults(defineProps<Props>(), {
  hasProceedAction: false,
  hasPaymentMethod: false,
  balanceCents: 0,
  trialDaysRemaining: 0,
});

const emit = defineEmits<{
  close: [];
  proceed: [];
  "purchase-credits": [];
}>();

const isCheckingOut = ref(false);
const selectedAmountCents = ref(2000);
const useCustomAmount = ref(false);
const customAmountDollars = ref(20);
const customInputRef = ref<HTMLInputElement | null>(null);

const presets = [
  { label: "$20", cents: 2000 },
  { label: "$50", cents: 5000 },
  { label: "$100", cents: 10000 },
  { label: "$200", cents: 20000 },
];

const effectiveAmountCents = computed(() =>
  useCustomAmount.value
    ? Math.round((customAmountDollars.value || 0) * 100)
    : selectedAmountCents.value
);

const isValidAmount = computed(() => {
  const c = effectiveAmountCents.value;
  return c >= 2000 && c <= 200000;
});

const formattedSelectedAmount = computed(() =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(effectiveAmountCents.value / 100)
);

function selectPreset(cents: number) {
  useCustomAmount.value = false;
  selectedAmountCents.value = cents;
}

function activateCustom() {
  useCustomAmount.value = true;
  nextTick(() => customInputRef.value?.focus());
}

const formattedBalance = computed(() =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(props.balanceCents / 100)
);

const handleClose = () => {
  emit("close");
};

const handleAddPaymentMethod = async () => {
  if (!isValidAmount.value || isCheckingOut.value) return;
  isCheckingOut.value = true;
  try {
    const result = await Billing.createCreditsCheckout(
      effectiveAmountCents.value,
      window.location.pathname
    );
    const url = result?.data?.data?.url;
    if (url) {
      sessionStorage.setItem("sb_stripe_redirect_pending", "true");
      window.location.href = url;
      // don't reset isCheckingOut — page is navigating away
    } else {
      toastService.error("Could not initiate checkout. Please try again.");
      isCheckingOut.value = false;
    }
  } catch {
    toastService.error("Could not initiate checkout. Please try again.");
    isCheckingOut.value = false;
  }
};
</script>

<style scoped>
.usage-modal {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px 24px 4px;
  max-width: 100%;
}

.usage-modal__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.usage-modal__text {
  font-size: 0.85rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.usage-modal__balance-strip {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  font-size: 0.82rem;
  color: #374151;
  flex-wrap: wrap;
}

.usage-modal__balance-icon {
  color: #059669;
  flex-shrink: 0;
  font-size: 15px;
}

.usage-modal__balance-strip strong {
  color: #065f46;
  font-weight: 600;
}

.usage-modal__balance--low {
  color: #b45309 !important;
}

.usage-modal__balance-note {
  color: #92400e;
  font-size: 0.78rem;
}

.usage-modal__features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 20px;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 12px;
}

.usage-modal__feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8125rem;
  color: #374151;
}

.usage-modal__feature-icon {
  font-size: 18px;
  color: #7c3aed;
  flex-shrink: 0;
}

.usage-modal__trial-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fcd34d;
  border-radius: 24px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #92400e;
}

.usage-modal__clock-icon {
  font-size: 13px;
}

.usage-modal__footer-btn {
  padding: 8px 20px !important;
  font-size: 0.8125rem !important;
}

.usage-modal__cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #7c3aed !important;
  border-color: #7c3aed !important;
  color: #ffffff !important;
  font-weight: 500;
}

.usage-modal__cta-btn:hover:not(:disabled) {
  background-color: #6d28d9 !important;
  border-color: #6d28d9 !important;
}

.usage-modal__btn-icon {
  font-size: 16px;
}

:deep(.ui-modal__footer) {
  padding: 14px 24px 20px;
}

.usage-modal__header-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}
.usage-modal__modal-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #170f49;
  line-height: 1.5;
}
.usage-modal__select-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}
.usage-modal__presets {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.usage-modal__preset-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 14px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
}
.usage-modal__preset-btn:hover {
  border-color: #c4b5fd;
  background: #faf5ff;
  color: #6d28d9;
}
.usage-modal__preset-btn--selected {
  border-color: #7c3aed;
  background: #f5f3ff;
  color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
}
.usage-modal__preset-btn--custom {
  font-size: 0.85rem;
  color: #6b7280;
}
.usage-modal__preset-btn--custom.usage-modal__preset-btn--selected {
  color: #7c3aed;
}
.usage-modal__custom-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.usage-modal__custom-field {
  display: flex;
  align-items: center;
  border: 1.5px solid #7c3aed;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
}
.usage-modal__currency-prefix {
  padding: 0 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  height: 42px;
  display: flex;
  align-items: center;
}
.usage-modal__custom-input {
  flex: 1;
  padding: 10px 12px;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: #111827;
  background: #fff;
}
.usage-modal__hint {
  font-size: 0.75rem;
  color: #9ca3af;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.usage-modal__btn-icon--spin {
  animation: spin 0.8s linear infinite;
}
</style>
